import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Restaurant } from '@shared/types/restaurant';
import { DEPOSIT_GUARANTEE } from '@features/booking/state/types';
import { MEITRE_GA_ID } from '@core/config/constants';
import { EMBED_EVENTS } from '@embed-protocol';
import { allowsRestaurantTags, emitEmbedEvent, getEmbedContext } from '@core/embed';
import { getAnalyticsEnvironment, ANALYTICS_ENV } from './getEnvironment';
import { loadGA4, loadGTM, loadFacebookPixel } from './scriptLoaders';
import type { AnalyticsContextValue, ConversionData } from './types';
import { createAnalyticsTrackers } from './createAnalyticsTrackers';
import { applyEmbeddedTagging } from './embeddedTagging';
import { AnalyticsContext } from './analyticsContext';
import { getTitleForPath } from './RouteTracker';
export { useAnalyticsContext } from './analyticsContext';

interface AnalyticsProviderProps {
  children: React.ReactNode;
  restaurant: Restaurant;
}

export function AnalyticsProvider({ children, restaurant }: AnalyticsProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const envRef = useRef(getAnalyticsEnvironment());
  const embedContextRef = useRef(getEmbedContext());
  const restaurantTagsAllowed = allowsRestaurantTags(embedContextRef.current);
  const restaurantGaIdRef = useRef('');

  // Empty when restaurant tags are off: that is what stops the second
  // page_view and the `reserva` conversion reaching the restaurant's GA.
  restaurantGaIdRef.current = restaurantTagsAllowed ? restaurant.googleAnalyticsKey || '' : '';

  // The v1 widget loads its iframe hidden on every page view of the
  // restaurant's site, opened or not. Measuring before the guest interacts
  // would count every visit to that site as a booking-app session.
  const [isActive, setIsActive] = useState(embedContextRef.current.mode !== 'widget_v1');
  const wasDeferredRef = useRef(!isActive);

  useEffect(() => {
    if (isActive) return;
    const activate = () => setIsActive(true);
    const options = { capture: true, once: true } as const;
    window.addEventListener('pointerdown', activate, options);
    window.addEventListener('keydown', activate, options);
    return () => {
      window.removeEventListener('pointerdown', activate, options);
      window.removeEventListener('keydown', activate, options);
    };
  }, [isActive]);

  // Shared page_view / event trackers. The factory encapsulates the identical
  // safeCall + LOCAL/PRODUCTION gating; 'v2' tags Meitre GA events and the
  // restaurant GA id drives the per-restaurant page_view double-send.
  // Memoized on stable refs so the context value stays referentially stable.
  const { trackPageView, trackEvent } = useMemo(
    () =>
      createAnalyticsTrackers({
        envRef,
        clientTag: 'v2',
        secondaryGaIdRef: restaurantGaIdRef,
      }),
    [],
  );

  useEffect(() => {
    if (!isActive) return;
    const env = envRef.current;

    if (env === ANALYTICS_ENV.PRODUCTION) {
      applyEmbeddedTagging(embedContextRef.current);

      // Meitre global GA (no auto page_view — RouteTracker handles it)
      loadGA4(MEITRE_GA_ID, false);

      if (restaurantTagsAllowed) {
        if (restaurant.googleAnalyticsKey) {
          loadGA4(restaurant.googleAnalyticsKey, false);
        }
        if (restaurant.googleTagManagerKey) {
          loadGTM(restaurant.googleTagManagerKey);
        }
        if (restaurant.facebookPixelKey) {
          loadFacebookPixel(restaurant.facebookPixelKey);
        }
      }

      // The route the guest was on when they first touched the widget was
      // never reported: nothing was loaded yet.
      if (wasDeferredRef.current) {
        wasDeferredRef.current = false;
        const { pathname } = window.location;
        trackPageView({ path: pathname, title: getTitleForPath(pathname) });
      }
    }

    setIsInitialized(true);
  }, [
    isActive,
    trackPageView,
    restaurantTagsAllowed,
    restaurant.googleAnalyticsKey,
    restaurant.googleTagManagerKey,
    restaurant.facebookPixelKey,
  ]);

  // trackConversion keeps its own safeCall — it stays inline because its
  // reservation_complete + reserva + fbq Schedule shape is provider-specific.
  // Identical defensive wrapper as the factory's: analytics failure must
  // never break the UI (caller may already be in an error path).
  const safeCall = (fn: () => void, context: string): void => {
    try {
      fn();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[Analytics] ${context} threw:`, err);
    }
  };

  const trackConversion = useCallback((data: ConversionData) => {
    // Independent of the analytics environment: the embedding site measures
    // with its own tags, and staging is where that integration is tested.
    safeCall(() => {
      if (data.isWaitingList) {
        emitEmbedEvent(EMBED_EVENTS.waitlistJoined, { party_size: data.diners }, `waitlist:${data.reservationId}`);
      } else {
        emitEmbedEvent(
          EMBED_EVENTS.reservationConfirmed,
          {
            transaction_id: data.uniqueId,
            party_size: data.diners,
            // A no-show guarantee is a card hold, not revenue.
            ...(data.guaranteeType === DEPOSIT_GUARANTEE && data.fee > 0 ? { value: data.fee, currency: data.currency } : {}),
          },
          `reservation:${data.reservationId}`,
        );
      }
    }, 'trackConversion(embed)');

    const env = envRef.current;

    if (env === ANALYTICS_ENV.LOCAL) {
      console.log('[Analytics] Conversion:', data);
      return;
    }

    if (env === ANALYTICS_ENV.PRODUCTION) {
      if (window.gtag) {
        safeCall(() => {
          window.gtag!('event', 'reservation_complete', {
            send_to: MEITRE_GA_ID,
            client: 'v2',
            reservation_id: data.reservationId,
            unique_id: data.uniqueId,
            party_size: data.diners,
            date: data.date,
            time: data.time,
            guarantee_type: data.guaranteeType,
            fee: data.fee,
            currency: data.currency,
            is_waiting_list: data.isWaitingList ?? false,
          });

          const restaurantGaId = restaurantGaIdRef.current;
          if (restaurantGaId) {
            window.gtag!('event', 'reserva', {
              event_category: 'meitre',
              send_to: restaurantGaId,
            });
          }
        }, 'trackConversion(gtag)');
      }

      if (restaurantTagsAllowed && window.fbq) {
        safeCall(() => {
          window.fbq!('track', 'Schedule');
        }, 'trackConversion(fbq)');
      }
    }
  }, [restaurantTagsAllowed]);

  const value: AnalyticsContextValue = useMemo(() => ({
    trackEvent,
    trackPageView,
    trackConversion,
    isInitialized,
  }), [trackEvent, trackPageView, trackConversion, isInitialized]);

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}

